/**
 * Financial Models panel — standalone module (own schema, not PPA/contracts).
 *
 * Lives inside the Documents page. Lists every uploaded RevFM workbook for
 * the portfolio; one is "Active" and drives the Facility/DSCR/Variance
 * screens. Supports:
 *   • Activate         — make this model drive the calc screens
 *   • Edit             — audited overrides: scalars, line-remap (fix a
 *                         mis-parsed row), covenant register
 *   • Compare          — side-by-side DSCR / CFADS / covenants
 *   • Delete
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Chip, IconButton, Tooltip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, Stack, Alert,
  CircularProgress, Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InsightsIcon from '@mui/icons-material/Insights';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtAud = (n) => (n == null ? '—' : `A$${Math.round(n).toLocaleString('en-AU')}`);
const fmtX = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}x`);
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-AU'));

const STATUS_COLOR = {
  parsed: { bg: 'rgba(34,160,90,0.10)', fg: '#1f9d57' },
  uploaded: { bg: 'rgba(59,130,246,0.10)', fg: '#2d7dd2' },
  error: { bg: 'rgba(229,56,79,0.12)', fg: '#e5384f' },
};

export default function FinancialModels({ portfolioId, embedded = false, onCountChange }) {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const openModel = (id) =>
    navigate(`/portfolios/${portfolioId}/financial-models/${id}`);

  const base = `${API}/api/portfolios/${portfolioId}/financial-models`;

  const fetchModels = useCallback(async () => {
    try {
      const r = await axios.get(base);
      setModels(r.data.models || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load financial models');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  // Surface count to parent (e.g. for tab badges) so the parent doesn't
  // need to double-fetch the models list.
  useEffect(() => {
    if (onCountChange) onCountChange(models.length);
  }, [models.length, onCountChange]);

  // Poll while any model is still parsing in the background.
  useEffect(() => {
    if (!models.some((m) => m.status === 'uploaded')) return undefined;
    const t = setInterval(fetchModels, 3000);
    return () => clearInterval(t);
  }, [models, fetchModels]);

  const activate = async (id) => {
    try { await axios.post(`${base}/${id}/activate`); fetchModels(); }
    catch (e) { setError(e.response?.data?.error || 'Activate failed'); }
  };
  const reparse = async (id) => {
    // Re-runs the parser against the .xlsb already in GCS. Use after a
    // parser upgrade (new line mappings, etc.) so the model picks up
    // the new extraction without re-uploading the workbook.
    try {
      await axios.post(`${base}/${id}/reparse`);
      // Backend flips status to 'uploaded' while it's re-parsing — the
      // existing useEffect poll will catch the transition to 'parsed'.
      fetchModels();
    } catch (e) {
      setError(e.response?.data?.error || 'Reparse failed');
    }
  };
  const remove = async (id) => {
    try { await axios.delete(`${base}/${id}`); fetchModels(); }
    catch (e) { setError(e.response?.data?.error || 'Delete failed'); }
  };
  const toggleCompare = (id) =>
    setCompareIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  }
  // When embedded inside a tab whose label already identifies this
  // section, render an empty-state instead of disappearing — otherwise
  // the user picks the tab and sees nothing.
  if (!models.length && !embedded) return null;

  return (
    <Box sx={{ mb: embedded ? 0 : 4 }} className="animate-in">
      {/* Section header — hidden when embedded (parent's tab label is
          the heading). Compare action moves to a right-aligned toolbar
          above the table when embedded so it's still reachable. */}
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <InsightsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Financial Models
          </Typography>
          <Chip size="small" label={`${models.length}`} sx={{ height: 20, fontSize: '0.7rem' }} />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" variant="outlined" startIcon={<CompareArrowsIcon />}
            disabled={compareIds.length < 2}
            onClick={() => setCompareOpen(true)}
          >
            Compare ({compareIds.length})
          </Button>
        </Stack>
      )}
      {embedded && models.length > 0 && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            size="small" variant="outlined" startIcon={<CompareArrowsIcon />}
            disabled={compareIds.length < 2}
            onClick={() => setCompareOpen(true)}
          >
            Compare ({compareIds.length})
          </Button>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {embedded && models.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: 'background.paper',
                     border: '1px solid', borderColor: 'divider' }}>
          <InsightsIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            No financial models yet.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, opacity: 0.7 }}>
            Upload a lender financial model (.xlsb / .xlsm / .xlsx) to drive Debt Metrics.
          </Typography>
        </Paper>
      ) : (
      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(217,138,43,0.06)' }}>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>SCENARIO / FILE</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>STATUS</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>DSCR (12m)</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>LOCK-UP</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((m) => {
              const s = m.summary_json || {};
              const sc = STATUS_COLOR[m.status] || STATUS_COLOR.uploaded;
              return (
                <TableRow
                  key={m.id} hover
                  onClick={() => m.status !== 'uploaded' && openModel(m.id)}
                  sx={{ cursor: m.status === 'uploaded' ? 'default' : 'pointer', '& td': { borderBottom: '1px solid', borderColor: 'divider' } }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={compareIds.includes(m.id)}
                      disabled={m.status !== 'parsed'}
                      onChange={() => toggleCompare(m.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {m.is_active && (
                        <Tooltip title="Active — drives Facility / DSCR / Variance">
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#1f9d57' }} />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.label || m.source_filename}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {m.source_filename}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.is_active ? 'ACTIVE' : m.status}
                      sx={{ bgcolor: m.is_active ? 'rgba(34,160,90,0.14)' : sc.bg, color: m.is_active ? '#1f9d57' : sc.fg, fontWeight: 700, fontSize: '0.66rem' }} />
                    {m.status === 'error' && (
                      <Tooltip title={m.error || 'parse error'}><Typography sx={{ fontSize: '0.66rem', color: '#e5384f', mt: 0.5 }}>parse failed</Typography></Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {fmtX(s.dscr_first_12m ?? s.lockup_dscr ? s.dscr_first_12m : null) }
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{fmtX(s.lockup_dscr)}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                      {!m.is_active && m.status === 'parsed' && (
                        <Tooltip title="Activate — make this model drive Debt Metrics / DSCR / Variance">
                          <IconButton size="small" onClick={() => activate(m.id)}
                                      sx={{ '&:hover': { color: '#1f9d57' } }}>
                            <PowerSettingsNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={m.status === 'uploaded'
                          ? 'Re-parsing…'
                          : 'Re-parse (apply latest parser to the .xlsb already in GCS)'}>
                        <span>
                          <IconButton size="small" disabled={m.status === 'uploaded'}
                                      onClick={() => reparse(m.id)}>
                            {m.status === 'uploaded'
                              ? <CircularProgress size={16} thickness={5} />
                              : <RefreshIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Open / correct parse">
                        <span><IconButton size="small" disabled={m.status === 'uploaded'} onClick={() => openModel(m.id)}><EditIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => remove(m.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {compareOpen && (
        <CompareDialog
          base={base}
          ids={compareIds}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </Box>
  );
}

// ── Compare dialog ──────────────────────────────────────────────────────────

function CompareDialog({ base, ids, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${base}/compare?ids=${ids.join(',')}`)
      .then((r) => setRows(r.data.models || []))
      .catch((e) => setErr(e.response?.data?.error || 'Compare failed'));
  }, [base, ids]);

  const series = useMemo(() => {
    if (!rows) return null;
    return rows
      .filter((m) => m.dscr_series?.length)
      .map((m) => ({
        label: (m.label || m.model_id || '').slice(0, 28),
        data: m.dscr_series.slice(0, 60).map((p) => p.dscr),
      }));
  }, [rows]);
  const xAxis = useMemo(() => {
    const f = rows?.find((m) => m.dscr_series?.length);
    return f ? f.dscr_series.slice(0, 60).map((p) => p.period_start) : [];
  }, [rows]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Model comparison</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error">{err}</Alert>}
        {!rows && !err && <CircularProgress size={22} />}
        {rows && (
          <Stack spacing={3}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                  {rows.map((m) => (
                    <TableCell key={m.model_id} sx={{ fontWeight: 700 }}>
                      {(m.label || m.model_id).slice(0, 26)}{m.is_active ? ' ★' : ''}
                    </TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {[
                    ['DSCR (first 12m)', (m) => fmtX(m.kpis?.dscr_first_12m)],
                    ['Lock-up DSCR', (m) => fmtX(m.kpis?.lockup_dscr)],
                    ['Default DSCR', (m) => fmtX(m.kpis?.default_dscr)],
                    ['All-in interest %', (m) => (m.kpis?.coupon_pct ?? '—')],
                    ['CFADS total', (m) => fmtAud(m.kpis?.cfads_total_aud)],
                    ['Debt service total', (m) => fmtAud(m.kpis?.debt_service_total_aud)],
                    ['Debt outstanding', (m) => fmtAud(m.kpis?.debt_outstanding_aud)],
                    ['P50 MWh', (m) => fmtNum(m.kpis?.p50_mwh)],
                    ['P90 MWh', (m) => fmtNum(m.kpis?.p90_mwh)],
                    ['Covenants', (m) => (m.covenants?.length ?? 0)],
                  ].map(([lbl, fn]) => (
                    <TableRow key={lbl}>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{lbl}</TableCell>
                      {rows.map((m) => (
                        <TableCell key={m.model_id} sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {m.error ? '—' : fn(m)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {series && series.length > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1 }}>DSCR trajectory</Typography>
                <LineChart
                  height={260}
                  xAxis={[{ scaleType: 'point', data: xAxis }]}
                  series={series.map((s) => ({ ...s, showMark: false }))}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
